// Calculadora del cost total d'una oportunitat i del rendiment (§8.8). Motor pur: el fan servir la
// pantalla (en viu) i qualsevol informe futur. Imports en cèntims (enters); percentatges com a nombres
// («8» vol dir 8 %, «1.2» vol dir 1,2 %).
export const COST_GROUPS = ['acquisition', 'works', 'legal', 'other'];
/** Partides habituals: l'etiqueta la tradueix la interfície. `custom` porta l'etiqueta escrita. */
export const COST_KEYS = [
    'notary', 'registry', 'gestoria', 'appraisal', 'commission',
    'reform', 'architect', 'legalization', 'licenses', 'custom',
];
export const COST_KEY_GROUP = {
    notary: 'acquisition', registry: 'acquisition', gestoria: 'acquisition', appraisal: 'acquisition', commission: 'acquisition',
    reform: 'works', architect: 'works',
    legalization: 'legal', licenses: 'legal',
};
/**
 * Trams de l'ITP de les Illes Balears, com a valor de partida. ⚠️ A VALIDAR amb la normativa vigent
 * (§14): l'agent els pot canviar a cada oportunitat.
 */
export const BALEARIC_ITP_BRACKETS = [
    { upToCents: 400_000_00, pct: 8 },
    { upToCents: 600_000_00, pct: 9 },
    { upToCents: 1_000_000_00, pct: 10 },
    { upToCents: null, pct: 13 },
];
export function defaultCostConfig() {
    const line = (id, key, kind = 'amount') => ({ id, key, group: COST_KEY_GROUP[key], label: '', kind, cents: 0, pct: 0 });
    return {
        taxMode: 'itp',
        itpBrackets: BALEARIC_ITP_BRACKETS.map((b) => ({ ...b })),
        vatPct: 10,
        ajdPct: 1.2,
        lines: [
            line('notary', 'notary'), line('registry', 'registry'), line('gestoria', 'gestoria'), line('appraisal', 'appraisal'),
            line('commission', 'commission', 'pct'), line('reform', 'reform'), line('legalization', 'legalization'),
        ],
        contingencyPct: 10,
        holdingMonths: 0,
        holdingMonthlyCents: 0,
        financing: { enabled: false, loanPct: 70, ratePct: 3.5, openingFeePct: 1 },
    };
}
export function defaultExitConfig() {
    return { mode: 'none', salePriceCents: 0, saleCostsPct: 5, monthlyRentCents: 0, annualExpensesCents: 0, vacancyPct: 5 };
}
const pctOf = (cents, pct) => Math.round((cents * pct) / 100);
/** ITP per trams: cada part del preu paga el percentatge del seu tram. */
export function itpTax(priceCents, brackets) {
    const sorted = [...brackets].sort((a, b) => (a.upToCents ?? Infinity) - (b.upToCents ?? Infinity));
    let from = 0;
    let tax = 0;
    for (const b of sorted) {
        const to = Math.min(priceCents, b.upToCents ?? Infinity);
        if (to > from)
            tax += ((to - from) * b.pct) / 100;
        from = Math.max(from, b.upToCents ?? Infinity);
        if (from >= priceCents)
            break;
    }
    return Math.round(tax);
}
/** Cost total de comprar a `priceCents`, amb tot el que hi ha a la configuració. */
export function opportunityCost(priceCents, cfg, areaM2 = null) {
    const taxes = [];
    if (cfg.taxMode === 'itp')
        taxes.push({ key: 'itp', cents: itpTax(priceCents, cfg.itpBrackets) });
    if (cfg.taxMode === 'vat') {
        taxes.push({ key: 'vat', cents: pctOf(priceCents, cfg.vatPct) });
        taxes.push({ key: 'ajd', cents: pctOf(priceCents, cfg.ajdPct) });
    }
    const taxCents = taxes.reduce((n, t) => n + t.cents, 0);
    const groups = { acquisition: 0, works: 0, legal: 0, other: 0 };
    const lines = cfg.lines.map((l) => {
        const cents = l.kind === 'pct' ? pctOf(priceCents, l.pct) : l.cents;
        groups[l.group] += cents;
        return { id: l.id, key: l.key, group: l.group, label: l.label, cents };
    });
    const contingencyCents = pctOf(groups.works, cfg.contingencyPct);
    const holdingCents = cfg.holdingMonths * cfg.holdingMonthlyCents;
    const f = cfg.financing;
    const loanCents = f.enabled ? pctOf(priceCents, f.loanPct) : 0;
    const interestCents = f.enabled ? Math.round((loanCents * f.ratePct * cfg.holdingMonths) / 1200) : 0;
    const openingFeeCents = f.enabled ? pctOf(loanCents, f.openingFeePct) : 0;
    const totalCents = priceCents + taxCents + lines.reduce((n, l) => n + l.cents, 0)
        + contingencyCents + holdingCents + interestCents + openingFeeCents;
    return {
        priceCents, taxes, taxCents, lines, groups, contingencyCents, holdingCents,
        loanCents, interestCents, openingFeeCents, totalCents,
        perM2Cents: areaM2 && areaM2 > 0 ? Math.round(totalCents / areaM2) : null,
        cashCents: totalCents - loanCents,
    };
}
const ratio = (a, b) => (b > 0 ? Math.round((a / b) * 10000) / 100 : null);
/**
 * Rendiment: amb una venda, el benefici, el marge sobre el cost total i la rendibilitat dels diners propis;
 * amb un lloguer, la rendibilitat bruta (lloguer anual ÷ cost) i la neta (descomptant buits i despeses).
 */
export function opportunityReturn(cost, exit) {
    if (exit.mode === 'sale') {
        const saleCostsCents = pctOf(exit.salePriceCents, exit.saleCostsPct);
        const netSaleCents = exit.salePriceCents - saleCostsCents;
        const profitCents = netSaleCents - cost.totalCents;
        return {
            sale: { saleCostsCents, netSaleCents, profitCents, marginPct: ratio(profitCents, cost.totalCents), cashReturnPct: ratio(profitCents, cost.cashCents) },
            rent: null,
        };
    }
    if (exit.mode === 'rent') {
        const annualGrossCents = exit.monthlyRentCents * 12;
        const annualNetCents = Math.round(annualGrossCents * (1 - exit.vacancyPct / 100)) - exit.annualExpensesCents;
        return {
            sale: null,
            rent: { annualGrossCents, annualNetCents, grossYieldPct: ratio(annualGrossCents, cost.totalCents), netYieldPct: ratio(annualNetCents, cost.totalCents) },
        };
    }
    return { sale: null, rent: null };
}
